import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { of } from "rxjs";

import { environment } from "../../environments/environment";
import { LoggerService } from './logger.service';

interface User {
  id_usuario: string;
  username: string;
  rol_id: number;
  rol_nombre: string;
}

interface LoginResponse {
  message: string;
  token: string;
  user: User;
}

interface VerifyTokenResponse {
  success: boolean;
  user?: User;
  message?: string;
}

@Injectable({
  providedIn: "root",
})
export class LoginService {
  private userSubject: BehaviorSubject<User>;
  private url_login = environment.api.login;
  private url_verify = environment.api.verificar_token;
  public user: Observable<User>;

  constructor(private router: Router, private http: HttpClient, private logger: LoggerService) {
    this.userSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem("user"))
    );
    this.user = this.userSubject.asObservable();

    // ✅ CAMBIO PRINCIPAL: Solo cargar desde localStorage, SIN peticiones HTTP
    this.loadUserFromStorage();
  }

  public get userValue(): User {
    return this.userSubject.value;
  }

  // ✅ NUEVO: Cargar usuario solo desde localStorage (sin HTTP)
  private loadUserFromStorage(): void {
    const token = localStorage.getItem("token");
    const userString = localStorage.getItem("user");

    if (token && userString) {
      try {
        const user = JSON.parse(userString);
        this.userSubject.next(user);
      } catch (error) {
        // Si hay error parseando, limpiar storage
        this.clearStorage();
      }
    }
  }

  login(username, password) {
    return this.http
      .post<LoginResponse>(`${this.url_login}`, {
        username,
        password,
      })
      .pipe(
        map((response) => {
          this.logger.log(response);

          localStorage.setItem("token", response.token);
          localStorage.setItem("user", JSON.stringify(response.user));
          this.userSubject.next(response.user);

          return response;
        }),
        catchError((error) => {
          this.logger.error('Error en login:', error);
          throw error;
        })
      );
  }

  // ✅ NUEVO: Verificación manual del token (solo cuando se necesite)
  public checkTokenValidity(): Observable<boolean> {
    const token = localStorage.getItem("token");

    if (!token) {
      return of(false);
    }

    return this.verifyToken(token).pipe(
      map((response) => {
        if (response.success && response.user) {
          // Actualizar datos del usuario si el token es válido
          this.userSubject.next(response.user);
          return true;
        } else {
          this.logout();
          return false;
        }
      }),
      catchError(() => {
        this.logout();
        return of(false);
      })
    );
  }

  // Método para verificar token (ahora público para uso manual)
  private verifyToken(token: string): Observable<VerifyTokenResponse> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.post<VerifyTokenResponse>(`${this.url_verify}`, {}, { headers });
  }

  getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem("token");
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  logout() {
    this.clearStorage();
    this.userSubject.next(null);
    this.router.navigate(["/login"]);
  }

  // ✅ NUEVO: Método helper para limpiar storage
  private clearStorage(): void {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  }

  isLogged(): boolean {
    const user = this.userValue;
    const token = localStorage.getItem("token");
    return !!(user && token);
  }

  isAdmin(): boolean {
    return this.userValue?.rol_nombre === 'Administrador';
  }

  isSecretary(): boolean {
    return this.userValue?.rol_nombre === 'Secretario';
  }

  hasPermission(allowedRoles: string[]): boolean {
    if (!this.userValue) return false;
    return allowedRoles.includes(this.userValue.rol_nombre);
  }

  getCurrentRole(): string | null {
    return this.userValue?.rol_nombre || null;
  }
}